import { useEffect, useMemo, useState } from "react";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useGetAllStatesQuery } from "../../data/states/statesApi";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import NumberFormat from "react-number-format";
import { skipToken } from "@reduxjs/toolkit/query";
import { showToast } from "../../../helpers/utils/Utils";
import PricingTableHead from "../../../components/common/PricingTableHead";
import { useGetAllCountyByStateIdQuery } from "../../data/counties/countiesApi";
import { useGetMetroAreaByStateIdQuery } from "../../data/metroAreas/metroAreasApi";
import { useGetAllTenantsQuery } from "../../customer/tenantApi";
import Pagination from "../../../components/common/pagination/Pagination";
import {
    useCreateVendorPricingMutation,
    useDeleteVendorPricingByIdMutation,
    useLazyExportVendorPricingDataQuery,
    useLazySearchVendorPricingQuery
} from "./vendorPricingApi";
import Preloader from "../../../components/common/Preloader";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import { FaTrash, FaUndo } from "react-icons/fa";
import { FaFileExport } from "react-icons/fa6";
import { useUtilDataQuery } from "../../profile/profileApi";


const VendorPricing = () => {

    useEffect(() => {
        document.title = "Partner Pricing";
    });
    const { data: utilData } = useUtilDataQuery("");
    const defaultPageSize = useMemo(() => 15, []);
    const { data: enums } = useGetEnumsQuery("");
    const [pricingQuery, setPricingQuery] = useState({});
    const [vendorPricingsQueryApi, vendorPricingQueryData] = useLazySearchVendorPricingQuery();
    const [description, setDescription] = useState("");
    const [productType, setProductType] = useState("");
    const [id, setId] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [sortOrder, setSortOrder] = useState("ASCENDING");
    const [sort, setSort] = useState("creationDate");
    const [state, setState] = useState("");
    const [tenant, setTenant] = useState(utilData?.customerLevel === 'SYS' ? "" : utilData?.tenant);
    const [county, setCounty] = useState("");
    const [metroArea, setMetroArea] = useState("");
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [descriptionAlert, setDescriptionAlert] = useState(false);
    const [systemGenerated, setSystemGenerated] = useState(false);
    const [alert, setAlert] = useState(false);
    const [isApplySearch, setIsApplySearch] = useState(false);
    const [showEditIcon, setShowEditIcon] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const statesQueryApi = useGetAllStatesQuery("");
    const tenantsQueryApi = useGetAllTenantsQuery("");
    const countyByStateId = useGetAllCountyByStateIdQuery(state || skipToken);
    const metroAreaByStateId = useGetMetroAreaByStateIdQuery(state || skipToken);
    const [deleteVendorPricing, { isLoading }] = useDeleteVendorPricingByIdMutation();
    const [vendor, setVendor] = useState("");
    const [vendorAlert, setVendorAlert] = useState(false);
    const [vendorCost, setVendorCost] = useState("");
    const [vendorPrice, setVendorPrice] = useState("");
    const [submitVendorPricing] = useCreateVendorPricingMutation();
    const [isExport, setIsExport] = useState(false);
    const [exportVendorPricingApi] = useLazyExportVendorPricingDataQuery();

    useEffect(() => {
        if (isApplySearch) {
            let query = getQuery();
            setPricingQuery(query)
            setIsApplySearch(false);
        }
    }, [isApplySearch]);


    function getQuery() {
        return {
            pageNumber: pageNumber,
            pageSize: pageSize,
            sort: sort,
            sortOrder: sortOrder,
            stateId: state,
            countyId: county,
            metroAreaId: metroArea,
            productType: productType,
            tenant: tenant,
            vendor: vendor
        }
    }

    useEffect(() => {
        if (isSubmit) {
            submitVendorPricing(input);
            setIsSubmit(false);
            setShowEditIcon(false);
            resetInput();
        }

    }, [isSubmit]);

    useEffect(() => {
        if (pricingQuery) {
            vendorPricingsQueryApi(pricingQuery);
        }
    }, [pricingQuery]);

    useEffect(() => {
        if (sort) {
            let query = getQuery();
            vendorPricingsQueryApi(query);
            setIsApplySearch(false);
        }
    }, [sort, sortOrder]);


    useEffect(() => {
        if (pageNumber >= 0) {
            setPricingQuery({ pageNumber: pageNumber })
        }
    }, [pageNumber]);

    useEffect(() => {
        if (pageSize) {
            setPricingQuery({ pageNumber: pageNumber, pageSize: pageSize })
        }
    }, [pageSize]);

    function showEditRow(obj: any) {
        setPageNumber(0);
        setId(obj.id);
        setDescription(obj.description);
        setVendor(obj.vendor);
        setProductType(obj.productType);
        setSystemGenerated(obj.systemGenerated);
        setTenant(obj.tenant);
        setState(obj.state != null ? obj.state.id : '');
        setCounty(obj.county != null ? obj.county.id : '');
        setMetroArea(obj.metroArea !== null ? obj.metroArea.id : '');
        setVendorPrice(obj.vendorPrice);
        setVendorCost(obj.vendorCost);
    }

    const [thead] = useState(
        utilData?.customerLevel !== null && utilData?.customerLevel === 'SYS' ?
            [
                { content: "Description", sortColumn: "description" },
                { content: "Product", sortColumn: "productType" },
                { content: "Vendor", sortColumn: "vendor" },
                { content: "Tenant", sortColumn: "tenant" },
                { content: "State", sortColumn: "state.stateName" },
                { content: "MSA", sortColumn: "metroArea.name" },
                { content: "County", sortColumn: "county.countyName" },
                { content: "Cost", sortColumn: "vendorCost" },
                { content: "Price", sortColumn: "vendorPrice" },
                { content: hasPermission(["PTPRC-D"]) ? "Action" : "", sortColumn: "" }
            ]
            :

            [
                { content: "Description", sortColumn: "description" },
                { content: "Product", sortColumn: "productType" },
                { content: "Vendor", sortColumn: "vendor" },
                { content: "State", sortColumn: "state.stateName" },
                { content: "MSA", sortColumn: "metroArea.name" },
                { content: "County", sortColumn: "county.countyName" },
                { content: "Cost", sortColumn: "vendorCost" },
                { content: "Price", sortColumn: "vendorPrice" },
                { content: hasPermission(["PTPRC-D"]) ? "Action" : "", sortColumn: "" }
            ]
    )


    const input = {
        id: id,
        description: description,
        productType: productType,
        tenant: tenant,
        county: county === '' ? null : { id: county },
        state: state === '' ? null : { id: state },
        vendor: vendor,
        metroArea: metroArea === '' ? null : { id: metroArea },
        vendorCost: vendorCost === '' ? 0.0 : vendorCost,
        vendorPrice: vendorPrice === '' ? 0.0 : vendorPrice,
    }

    function handleSubmit(e: any) {
        e.preventDefault();
        if (input.productType === '') {
            showToast("Product Type must be selected !", "error");
            setAlert(true);
        } else if (input.vendor === '') {
            showToast("Vendor must be selected!", "error");
            setVendorAlert(true);
        } else if (input.county !== null && input.metroArea !== null) {
            return showToast("Both County and Metro Area cannot be selected");
        } else {
            setAlert(false);
            setIsSubmit(true);
        }
    }


    function resetInput() {
        setId(null);
        setDescription("");
        setProductType("");
        setTenant(utilData?.customerLevel === 'SYS' ? "" : utilData?.tenant);
        setState("");
        setCounty("");
        setMetroArea("");
        setPricingQuery({});
        setVendor("");
        vendorPricingsQueryApi(pricingQuery)
    }


    const handleDelete = async (vendorId: any) => {
        if (vendorId) {
            await deleteVendorPricing(vendorId).unwrap();
        }
    };

    const exportVendorPricing = async () => {
        const query = getQuery();
        await exportVendorPricingApi(query);
        setIsExport(false);
    };
    return (
        <>
            {vendorPricingQueryData.data === undefined
                ? <Preloader />
                :
                <>
                    <div className="gap-1 text-text-0 flex justify-end absolute right-[10px] top-0">
                        <IconButton icon={<FaFileExport />}
                            hasBackground={true}
                            title={"Export Pricing"}
                            onClick={(e: any) => {
                                if (!isExport) {
                                    e.preventDefault();
                                    setIsExport(true);
                                    exportVendorPricing();
                                }
                            }}
                            className={`px-3 py-2 rounded-xl ${isExport ? "bg-surface-4 export" : " export"}`}
                        />
                        <IconButton icon={<FaUndo size={14} />}
                            hasBackground={true}
                            title={"Reset Filter"}
                            onClick={() => {
                                setShowEditIcon(false);
                                setSystemGenerated(false);
                                resetInput();
                            }}
                            className={'px-3 py-2 rounded-xl '}
                        />
                    </div>
                    <div className={"overflow-auto"}>
                        <table className="p-3 w-full">
                            {!vendorPricingQueryData.data && <thead>
                                <tr>
                                    {thead?.map((header) => {
                                        return (<th scope="col" className={'bg-highlight-0 text-text-0'}><a
                                            className="toggle-data-header toggle-data cursor">{header.content}<i
                                                className="fas fa-sort" /></a></th>
                                        )
                                    })
                                    }
                                </tr>
                            </thead>
                            }

                            {vendorPricingQueryData.data &&
                                <PricingTableHead headers={thead} data={vendorPricingQueryData.data} setSort={setSort}
                                    sortOrder={sortOrder} setSortOrder={setSortOrder}

                                />}
                            <tbody>
                                {hasPermission(["PTPRC-C"]) &&
                                    <tr className="bg-text-1 hover:bg-text-1 transition cursor-pointer">
                                        <td>
                                            <textarea
                                                className={descriptionAlert ? "alertClass form-control table_data1" : "form-control table_data1 border border-surface-5"}
                                                placeholder="Enter Description..."
                                                value={description}
                                                onChange={(e: any) => {
                                                    setDescription(e.target.value);
                                                    setDescriptionAlert(false);
                                                }}
                                                disabled={systemGenerated}
                                            />
                                        </td>
                                        <td>
                                            <div className="form-group">
                                                <select
                                                    className={alert ? "alertClass form-control" : "form-control  border border-surface-5"}
                                                    value={productType}
                                                    onChange={(e: any) => {
                                                        setProductType(e.target.value);
                                                        setAlert(false);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}>
                                                    <option value="">Select Product...</option>
                                                    {enums?.["ProductType"].filter((p: any) => !p.value.includes("UNKNOWN"))?.map((curEnum: any) => {
                                                        return (
                                                            <option value={curEnum.constant}>{curEnum.value}</option>
                                                        )
                                                    }
                                                    )}
                                                </select>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="form-group ">
                                                <select
                                                    className={vendorAlert ? "alertClass  border border-surface-5 form-control" : "form-control  border border-surface-5"}
                                                    value={vendor}
                                                    onChange={(e) => {
                                                        setVendor(e.target.value);
                                                        setVendorAlert(false);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Select Vendor...</option>
                                                    {enums?.["VendorEnum"] && enums?.["VendorEnum"].filter((p: any) => !p.value.includes("STUB")).map((curEnum: any) => {
                                                        return (
                                                            <option value={curEnum.constant}>{curEnum.value}</option>
                                                        )
                                                    }
                                                    )}
                                                </select>
                                            </div>
                                        </td>


                                        {utilData?.customerLevel === 'SYS' && (<td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={tenant}
                                                    onChange={(e) => {
                                                        setTenant(e.target.value)
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {tenantsQueryApi.data && tenantsQueryApi.data?.map((curTenant: any) => {
                                                        return (
                                                            <>
                                                                <option
                                                                    value={curTenant.name}>{curTenant.name}</option>
                                                            </>
                                                        )
                                                    })}
                                                </select>
                                            </div>
                                        </td>)
                                        }


                                        <td>
                                            <div className="form-group">
                                                <select className="form-control  border border-surface-5" value={state}
                                                    onChange={(e: any) => {
                                                        setState(e.target.value)
                                                        setCounty('');
                                                        setMetroArea('');
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {statesQueryApi.data && statesQueryApi.data.error === undefined && statesQueryApi?.data?.map((curState: any) => {
                                                        return (
                                                            <>
                                                                <option
                                                                    value={curState.id}>{curState.stateName}</option>
                                                            </>
                                                        )
                                                    })}
                                                </select>
                                            </div>
                                        </td>
                                        {state === '' ?
                                            <td>
                                                <div className="form-group">
                                                    <select className="form-control  border border-surface-5"
                                                        value={metroArea}
                                                        onChange={(e: any) => {
                                                            setMetroArea(e.target.value)
                                                            setIsApplySearch(true);
                                                        }}
                                                        disabled={systemGenerated}
                                                    >
                                                        <option value="">Any</option>
                                                    </select>
                                                </div>
                                            </td>

                                            :
                                            <td>
                                                <div className="form-group">
                                                    <select className="form-control  border border-surface-5"
                                                        value={metroArea}
                                                        onChange={(e) => {
                                                            setMetroArea(e.target.value);
                                                            setIsApplySearch(true);
                                                        }}
                                                        disabled={systemGenerated}
                                                    >
                                                        <option value="">Any</option>
                                                        {state !== '' && metroAreaByStateId.data && metroAreaByStateId.data?.map((curMetroArea: any) => {
                                                            return (
                                                                <>
                                                                    <option
                                                                        value={curMetroArea.id}>{curMetroArea.name}</option>
                                                                </>
                                                            )
                                                        })}
                                                    </select>
                                                </div>
                                            </td>
                                        }
                                        {state === '' ?

                                            <td>
                                                <div className="form-group">
                                                    <select className="form-control  border border-surface-5" value={county}
                                                        onChange={(e) => {
                                                            setCounty(e.target.value);
                                                            setIsApplySearch(true);
                                                        }}
                                                        disabled={systemGenerated}
                                                    >
                                                        <option value="">Any</option>
                                                    </select>
                                                </div>
                                            </td>
                                            :
                                            <td>
                                                <div className="form-group"><select
                                                    className="form-control  border border-surface-5" value={county}
                                                    onChange={(e) => {
                                                        setCounty(e.target.value);
                                                        setIsApplySearch(true);
                                                    }}
                                                    disabled={systemGenerated}
                                                >
                                                    <option value="">Any</option>
                                                    {state !== '' && countyByStateId.data && countyByStateId.data?.map((curCounty: any) => {
                                                        return (
                                                            <>
                                                                <option
                                                                    value={curCounty.id}>{curCounty.countyName}</option>

                                                            </>
                                                        )
                                                    })}
                                                </select>
                                                </div>
                                            </td>
                                        }

                                        <td>
                                            <NumberFormat placeholder='0.00'
                                                className="form-control text-center border border-surface-5"
                                                decimalScale={2}
                                                decimalSeparator="."
                                                fixedDecimalScale
                                                prefix={"$"}
                                                value={vendorCost}
                                                thousandSeparator={true}
                                                onChange={(e: any) => {
                                                    setVendorCost(e.target.value.replace('$', '').replaceAll(',', ''))
                                                }}
                                            />

                                        </td>
                                        <td>
                                            <NumberFormat placeholder='0.00'
                                                className="form-control text-center border border-surface-5"
                                                decimalScale={2}
                                                decimalSeparator="."
                                                fixedDecimalScale
                                                prefix={"$"}
                                                value={vendorPrice}
                                                thousandSeparator={true}
                                                onChange={(e: any) => {
                                                    setVendorPrice(e.target.value.replace('$', '').replaceAll(',', ''))
                                                }}
                                            />

                                        </td>
                                        <td>
                                            <i className={!showEditIcon ? 'fa ml-3 mt-2 element icon fa-plus-circle cursor_hover text-highlight-0' : 'fa ml-3 mt-2 element icon fa-edit cursor_hover text-highlight-0'}
                                                title="Add Price ..." onClick={handleSubmit} />
                                        </td>
                                    </tr>}

                                {vendorPricingQueryData.data && vendorPricingQueryData.data.data !== undefined && vendorPricingQueryData.data.data.length === 0 &&
                                    <tr>
                                        <td colSpan={9}>There are No vendor pricing to display</td>
                                    </tr>

                                }
                                {vendorPricingQueryData.data && vendorPricingQueryData.data.error === undefined ? vendorPricingQueryData.data.data !== undefined && vendorPricingQueryData.data.data?.map((curPricing: any, index: any) => {
                                    return (
                                        <tr key={index} className="odd:bg-text-1 hover:bg-text-1 transition cursor-pointer">
                                            <td>

                                                {hasPermission(["PTPRC-U"]) ?
                                                    <a className="text-highlight-4 active cursor-pointer" onClick={() => {
                                                        setShowEditIcon(true);
                                                        setAlert(false);
                                                        showEditRow(curPricing);
                                                        setIsApplySearch(true);
                                                    }}>{curPricing.description}</a> : <>{curPricing.description}</>
                                                }

                                            </td>

                                            <td className="text-center">
                                                {enums?.["ProductType"]?.map((curEnum: any) => {

                                                    if (curEnum.constant === curPricing.productType) return curEnum.value
                                                })}
                                            </td>

                                            <td>{curPricing.partner !== null && curPricing.partner !== '' ?
                                                enums?.["VendorEnum"]?.map((curEnum: any) => {
                                                    if (curEnum.constant === curPricing.vendor) return curEnum.value
                                                })
                                                : 'Any'}
                                            </td>

                                            {utilData?.customerLevel === 'SYS' &&
                                                <td>{curPricing.tenant !== null && curPricing.tenant !== '' ? curPricing.tenant : 'Any'}</td>}
                                            <td>{curPricing.state !== null && curPricing.state !== '' && curPricing.state.stateName !== null && curPricing.state.stateName !== '' ? curPricing.state.stateName : 'Any'}</td>
                                            <td>{curPricing.metroArea !== null && curPricing.metroArea !== '' && curPricing.metroArea.name !== null && curPricing.metroArea.name !== '' ? curPricing.metroArea.name : 'Any'}</td>
                                            <td>{curPricing.county !== null && curPricing.county !== '' && curPricing.county.countyName !== null && curPricing.county.countyName !== '' ? curPricing.county.countyName : 'Any'}</td>
                                            <td>

                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.vendorCost}
                                                    disabled={true}
                                                />

                                            </td>
                                            <td>

                                                <NumberFormat placeholder='0.00'
                                                    className="form-control text-center border border-surface-5"
                                                    decimalScale={2}
                                                    decimalSeparator="."
                                                    fixedDecimalScale
                                                    prefix={"$"}
                                                    thousandSeparator={true}
                                                    value={curPricing.vendorPrice}
                                                    disabled={true}
                                                />

                                            </td>
                                            <td>
                                                {(curPricing?.systemGenerated === false) && hasPermission(["PTPRC-D"]) &&
                                                    <ModalButton
                                                        triggerButton={
                                                            <IconButton
                                                                icon={<FaTrash />}
                                                                title={"Delete Vendor Pricing"}
                                                                hasBackground={false}
                                                                className="mr-2 text-highlight-0"
                                                            />
                                                        }
                                                        modal={
                                                            <ConfirmationModal
                                                                show={true}
                                                                setShow={() => {
                                                                }}
                                                                title={"Delete Vendor Pricing"}
                                                                bodyMessage={"Are you sure you want to delete this Vendor Pricing?"}
                                                                onSubmit={() => handleDelete(curPricing.id)}
                                                                isLoading={isLoading}
                                                            />
                                                        }
                                                    />}
                                            </td>
                                        </tr>
                                    )
                                }) :
                                    "no data found"
                                }

                            </tbody>
                        </table>
                    </div>
                    {
                        (vendorPricingQueryData.data && vendorPricingQueryData.data.totalPages != null && vendorPricingQueryData.data.totalPages > 0) ?
                            <Pagination
                                displayText={vendorPricingQueryData.data && vendorPricingQueryData.data.displayingText}
                                pageCount={vendorPricingQueryData.data && vendorPricingQueryData.data.totalPages}
                                setPageNumber={setPageNumber}
                                pageNumber={pageNumber}
                                groupName="Vendor-Pricing"
                                setExternalPageSize={setPageSize}
                                permissions={"-"}
                            />
                            : ''
                    }
                </>
            }
        </>
    )
}

export default VendorPricing;